/**
 * Record a payment against an existing subscription.
 *
 * Opens pre-filled with the exact outstanding balance, because settling up in
 * full is what happens most of the time — one tap of Save and it is done.
 */
import { useState } from 'react';
import { recordPayment } from '../db/operations';
import MoneyInput from './MoneyInput';
import { formatEgp } from '@shared/money';
import { classLabel } from '@shared/catalog';
import { PAYMENT_METHOD_LABELS, PAYMENT_METHODS, type PaymentMethod } from '@shared/types';
import type { SubscriptionRow } from '../db/queries';

export default function RecordPaymentDialog({
  row,
  onClose,
}: {
  row: SubscriptionRow;
  onClose: () => void;
}) {
  const [amount, setAmount] = useState<number | null>(row.remaining);
  const [method, setMethod] = useState<PaymentMethod>('cash');
  const [receivedBy, setReceivedBy] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function save() {
    if (amount === null || amount <= 0) {
      setError('Enter an amount');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await recordPayment({
        subscriptionId: row.subscription.id,
        memberId: row.member.id,
        amountPiastres: amount,
        method,
        receivedBy: receivedBy.trim() || 'Front desk',
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save');
      setSaving(false);
    }
  }

  return (
    <div
      className="no-print fixed inset-0 z-50 flex items-end justify-center bg-black/80 p-4 sm:items-center"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div className="card w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <h2 className="mb-1 text-2xl text-brand">Record payment</h2>
        <p className="mb-5 text-sm text-white/50">
          {row.member.full_name} · {classLabel(row.subscription.class_key)}
        </p>

        <div className="mb-4 flex justify-between rounded-btn border border-edge p-3 text-sm">
          <span className="text-white/50">Outstanding</span>
          <span className="tabular font-bold text-status-due">{formatEgp(row.remaining)}</span>
        </div>

        <label className="label" htmlFor="payAmount">
          Amount
        </label>
        <MoneyInput id="payAmount" valuePiastres={amount ?? 0} onChange={setAmount} autoFocus />

        <div className="mt-4">
          <div className="label">Method</div>
          <div className="flex gap-2">
            {PAYMENT_METHODS.map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setMethod(m)}
                className={`min-h-touch flex-1 rounded-btn border-2 text-sm font-bold uppercase
                            tracking-button transition-all duration-brand ${
                              method === m
                                ? 'border-brand bg-brand text-paper'
                                : 'border-edge bg-surface hover:border-brand'
                            }`}
              >
                {PAYMENT_METHOD_LABELS[m]}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-4">
          <label className="label" htmlFor="payBy">
            Received by
          </label>
          <input
            id="payBy"
            className="field"
            value={receivedBy}
            onChange={(e) => setReceivedBy(e.target.value)}
            placeholder="Your name"
          />
        </div>

        {error && <p className="mt-3 text-sm text-status-expired">{error}</p>}

        <div className="mt-6 flex gap-3">
          <button onClick={onClose} className="btn-ghost flex-1">
            Cancel
          </button>
          <button onClick={save} disabled={saving} className="btn flex-1">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>

        <p className="mt-4 text-center text-xs text-white/25">
          Payments are a permanent record — they are never edited or deleted.
          A mistake is corrected by recording a negative amount.
        </p>
      </div>
    </div>
  );
}
